/* The 340px settings panel for one dashboard widget: Title and Show title bar for
   every widget, then the type's own fields, a "how it fits" note, and Remove widget. */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../components/ChatAssistant/compact/tokens';
import { font as f } from '../components/ChatAssistant/compact/style';
import { Icon, Toggle } from '../components/ChatAssistant/compact/kit';
import {
  ACCOUNT_COLS, ATTENTION_KINDS, KPI_KEYS, TYPES, chartSplits,
  type Access, type AnyCfg, type Widget,
} from './model';
import { useWidgetTitle } from './titles';

interface Props {
  widget: Widget;
  access: Access;
  onChange: (patch: Partial<AnyCfg>) => void;
  onRemove: () => void;
  onClose: () => void;
}

function Field({ label, hint, children }: { label: string; hint?: string; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ font: f(600, 12.5), color: C.text }}>{label}</span>
        {hint && <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>}
      </span>
      {children}
    </div>
  );
}

function ToggleField({ label, hint, on, onChange }: { label: string; hint?: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ font: f(600, 12.5), color: C.text }}>{label}</span>
        {hint && <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>}
      </span>
      <Toggle on={on} onChange={onChange} title={label} />
    </label>
  );
}

interface Opt<T> { id: T; label: string; disabled?: boolean; title?: string }

function SegField<T extends string | number>({ label, hint, value, options, onChange }: { label: string; hint?: string; value: T; options: Opt<T>[]; onChange: (v: T) => void }) {
  return (
    <Field label={label} hint={hint}>
      <div role="radiogroup" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', padding: 2, background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, gap: 2 }}>
        {options.map((o) => {
          const on = o.id === value;
          return (
            <button
              key={String(o.id)}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={o.disabled}
              title={o.title}
              onClick={() => onChange(o.id)}
              style={{ flex: '1 1 auto', height: 26, padding: '0 9px', border: 0, borderRadius: 6, background: on ? C.segActive : 'transparent', color: on ? C.text : C.sub, opacity: o.disabled ? 0.4 : 1, font: f(500, 12), cursor: o.disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

function ChipsField<T extends string>({ label, hint, value, options, onChange }: { label: string; hint?: string; value: T[]; options: Opt<T>[]; onChange: (v: T[]) => void }) {
  return (
    <Field label={label} hint={hint}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((o) => {
          const i = value.indexOf(o.id);
          const on = i >= 0;
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(on ? value.filter((x) => x !== o.id) : [...value, o.id])}
              style={{ height: 28, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${on ? C.tintLine45 : C.borderHi}`, borderRadius: 99, background: on ? C.tint16 : 'transparent', color: on ? C.purplePale : C.sub, font: f(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {on && <span style={{ width: 16, height: 16, borderRadius: 99, background: C.purple, color: '#fff', font: f(700, 9.5, 16 / 9.5), textAlign: 'center' }}>{i + 1}</span>}
              {o.label}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

export function SettingsPanel({ widget, access, onChange, onRemove, onClose }: Props) {
  const { t } = useTranslation('dashboardWidgets');
  const defaultTitle = useWidgetTitle()({ ...widget, cfg: { ...widget.cfg, title: '' } } as Widget);
  const spec = TYPES[widget.type];
  const periods = (ids: number[]) => ids.map((id) => ({ id, label: t(`period.${id}`) }));
  const set = (patch: Record<string, unknown>) => onChange(patch as Partial<AnyCfg>);

  let fields: ReactNode = null;
  switch (widget.type) {
    case 'kpi': {
      const c = widget.cfg;
      fields = (
        <>
          <SegField label={t('settings.period')} value={c.per} options={periods([0, 1, 2])} onChange={(per) => set({ per })} />
          <ChipsField label={t('settings.numbers')} hint={t('settings.numbersHint')} value={c.metrics} options={KPI_KEYS.map((id) => ({ id, label: t(`kpi.${id}`) }))} onChange={(metrics) => set({ metrics })} />
          <ToggleField label={t('settings.delta')} on={c.delta} onChange={(delta) => set({ delta })} />
        </>
      );
      break;
    }
    case 'chart': {
      const c = widget.cfg;
      const splits = chartSplits(c.metric);
      fields = (
        <>
          <SegField label={t('settings.metric')} value={c.metric} options={(['rev', 'ppv', 'chats', 'msgs'] as const).map((id) => ({ id, label: t(`chart.metric.${id}`) }))} onChange={(metric) => set({ metric, split: chartSplits(metric).includes(c.split) ? c.split : 'none' })} />
          <SegField label={t('settings.splitBy')} hint={t('settings.splitHint')} value={splits.includes(c.split) ? c.split : 'none'} options={(['none', 'seller', 'type'] as const).map((id) => ({ id, label: t(`chart.split.${id}`), disabled: !splits.includes(id), title: !splits.includes(id) ? t('settings.splitHint') : undefined }))} onChange={(split) => set({ split })} />
          <SegField label={t('settings.period')} hint={t('settings.chartPeriodHint')} value={c.per} options={periods([1, 2]) as Opt<1 | 2>[]} onChange={(per) => set({ per })} />
          <ToggleField label={t('settings.summary')} hint={t('settings.summaryHint')} on={c.summary} onChange={(summary) => set({ summary })} />
        </>
      );
      break;
    }
    case 'split': {
      const c = widget.cfg;
      const chatsOk = c.by === 'account';
      fields = (
        <>
          <SegField label={t('settings.by')} hint={t('settings.byHint')} value={c.by} options={(['seller', 'type', 'account'] as const).map((id) => ({ id, label: t(`split.by.${id}`) }))} onChange={(by) => set({ by })} />
          <SegField label={t('settings.metric')} hint={chatsOk ? undefined : t('settings.splitMetricHint')} value={chatsOk ? c.metric : 'rev'} options={(['rev', 'chats'] as const).map((id) => ({ id, label: t(`split.metric.${id}`), disabled: id === 'chats' && !chatsOk }))} onChange={(metric) => set({ metric })} />
          <SegField label={t('settings.style')} value={c.style} options={(['bars', 'bar'] as const).map((id) => ({ id, label: t(`split.style.${id}`) }))} onChange={(style) => set({ style })} />
          <SegField label={t('settings.period')} value={c.per} options={periods([0, 1, 2])} onChange={(per) => set({ per })} />
        </>
      );
      break;
    }
    case 'accounts': {
      const c = widget.cfg;
      const cols = ACCOUNT_COLS.filter((id) => (id !== 'rev' && id !== 'ai' && id !== 'chats') || access.stats);
      fields = (
        <>
          <SegField label={t('settings.style')} value={c.style} options={(['list', 'tiles'] as const).map((id) => ({ id, label: t(`accounts.style.${id}`) }))} onChange={(style) => set({ style })} />
          {c.style === 'list' && (
            <ChipsField label={t('settings.columns')} hint={t('settings.columnsHint')} value={c.cols.filter((x) => cols.includes(x))} options={cols.map((id) => ({ id, label: t(`accounts.col.${id}`) }))} onChange={(next) => set({ cols: next })} />
          )}
          <SegField label={t('settings.sort')} value={c.sort} options={(['att', 'rev', 'name'] as const).filter((id) => id !== 'rev' || access.stats).map((id) => ({ id, label: t(`accounts.sort.${id}`) }))} onChange={(sort) => set({ sort })} />
          <SegField label={t('settings.show')} value={c.show} options={(['all', 'att', 'off'] as const).map((id) => ({ id, label: t(`accounts.show.${id}`) }))} onChange={(show) => set({ show })} />
          {access.stats && <SegField label={t('settings.period')} hint={t('settings.accountsPeriodHint')} value={c.per} options={periods([0, 1, 2])} onChange={(per) => set({ per })} />}
        </>
      );
      break;
    }
    case 'needs': {
      const c = widget.cfg;
      fields = (
        <>
          <ChipsField label={t('settings.include')} hint={t('settings.includeHint')} value={c.inc} options={ATTENTION_KINDS.map((id) => ({ id, label: t(`needs.kind.${id}`) }))} onChange={(inc) => set({ inc })} />
          <ToggleField label={t('settings.urgent')} hint={t('settings.urgentHint')} on={c.urgent} onChange={(urgent) => set({ urgent })} />
        </>
      );
      break;
    }
    case 'team': {
      const c = widget.cfg;
      fields = (
        <>
          <SegField label={t('settings.rank')} value={c.rank} options={(['rev', 'chats'] as const).map((id) => ({ id, label: t(`team.rank.${id}`) }))} onChange={(rank) => set({ rank })} />
          <ToggleField label={t('settings.aiRow')} on={c.ai} onChange={(ai) => set({ ai })} />
          <SegField label={t('settings.period')} value={c.per} options={periods([0, 1, 2])} onChange={(per) => set({ per })} />
        </>
      );
      break;
    }
  }

  return (
    <aside className="dbw-panel" aria-label={t('settings.aria')}>
      <div style={{ flex: 'none', padding: '16px 18px 14px', display: 'flex', alignItems: 'flex-start', gap: 12, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ width: 34, height: 34, flex: 'none', borderRadius: 8, background: C.tint16, color: C.purplePale, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={spec.icon} size={16} />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ font: f(600, 15), color: C.text }}>{t(`types.${widget.type}.name`)}</span>
          <span style={{ font: f(400, 12), color: C.sub }}>{t(`types.${widget.type}.desc`)}</span>
          <span style={{ font: f(500, 11.5), color: C.muted }}>{t('settings.size', { w: widget.w, h: widget.h, mw: spec.min[0], mh: spec.min[1] })}</span>
        </span>
        <button type="button" className="dbw-icon-btn" onClick={onClose} title={t('settings.close')} aria-label={t('settings.close')} style={{ width: 28, height: 28, color: C.sub }}>
          <Icon name="x" size={15} />
        </button>
      </div>
      <div className="dbw-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Field label={t('settings.title')}>
          <input
            id="dbw-title"
            value={widget.cfg.title}
            maxLength={60}
            placeholder={defaultTitle}
            onChange={(e) => set({ title: e.target.value })}
            style={{ height: 30, boxSizing: 'border-box', padding: '0 9px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.text, font: f(400, 12.5), outline: 'none' }}
          />
        </Field>
        <ToggleField label={t('settings.showTitle')} on={widget.cfg.hdr} onChange={(hdr) => set({ hdr })} />
        {fields}
        <div style={{ padding: '10px 12px', borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, display: 'flex', gap: 8, font: f(400, 11.5, 1.5), color: C.sub }}>
          <Icon name="maximize-2" size={13} color={C.purpleLt} style={{ marginTop: 2 }} />
          <span>{t(`fit.${widget.type}`)}</span>
        </div>
      </div>
      <div style={{ flex: 'none', padding: '12px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" className="dbw-ghost-btn" onClick={onRemove} style={{ height: 30, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 6, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, font: f(500, 12.5), cursor: 'pointer' }}>
          <Icon name="trash-2" size={13} />{t('settings.remove')}
        </button>
        <span style={{ flex: 1 }} />
        <button type="button" className="dbw-primary" onClick={onClose} style={{ height: 30, padding: '0 16px' }}>{t('settings.done')}</button>
      </div>
    </aside>
  );
}
